import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import LogoutNavbar from '../../components/LogoutNavbar';
import GlobalSidebar from '../../components/GlobalSidebar';
import PostLoginFooter from '../../components/PostLoginFooter';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../styles/AdminDashboard.css';
import '../../styles/ManageClubMembers.css';
import api from '../../api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Import the same icons used in other components for consistency
import {
  AccountCircle as ProfileIcon,
  FindInPage as LostFoundIcon,
  Lightbulb as SkillsIcon,
  Help as DoubtsIcon,
  Settings as SettingsIcon,
  Groups as ClubsIcon,
  Event as EventsIcon,
  PersonAdd as AddMemberIcon,
  People as ManageMembersIcon,
} from '@mui/icons-material';

const normalizeText = (value) => (value ?? '').toString().trim().toLowerCase();

// Backend often sends club as "Club Name (College)" via StringRelatedField.
// Keep UI/filtering resilient by stripping trailing parenthetical.
const stripClubSuffix = (value) => {
  const text = (value ?? '').toString().trim().replace(/\s+/g, ' ');
  return text.replace(/\s*\(.*\)\s*$/, '').trim();
};

const normalizeClubKey = (value) => normalizeText(stripClubSuffix(value));

const normalizeStatusKey = (value) => {
  const raw = (value ?? '').toString().trim().toLowerCase();
  if (!raw) return 'pending';
  if (raw === 'suspended') return 'inactive';
  return raw;
};

const extractClubLabel = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    return (
      value?.name ??
      value?.club_name ??
      value?.club?.name ??
      value?.club?.club_name ??
      ''
    );
  }
  return '';
};

export default function ManageClubMembers() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // When opened from Manage Clubs ("Manage Members" action), we want:
  // Dashboard > Clubs > Club Members, and back arrow should return to Clubs.
  const [navFrom, setNavFrom] = useState(() =>
    location?.state?.from === 'clubs' ? 'clubs' : 'dashboard'
  );

  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [nameFilter, setNameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [clubFilter, setClubFilter] = useState('');
  const [lockedClubFilter, setLockedClubFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const mainContentRef = useRef(null);
  const [menuWidths, setMenuWidths] = useState({ club: null, status: null });
  const [openSelectField, setOpenSelectField] = useState(null);

  // Prevent URL-sync from wiping query params before we read them.
  const [hydratedFromUrl, setHydratedFromUrl] = useState(false);

  const [filtersOpen, setFiltersOpen] = useState(!isMobile);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'approve' | 'suspend'
  const [actionLoading, setActionLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Define sidebar items for mobile menu
  const sidebarItems = [
    { 
      label: 'Profile', 
      path: '/admin/profile', 
      action: () => navigate('/admin/profile'),
      icon: <ProfileIcon />
    },
    { 
      label: 'Manage Clubs', 
      path: '/admin/clubs', 
      action: () => navigate('/admin/clubs'),
      icon: <ClubsIcon />
    },
    { 
      label: 'Add Club', 
      path: '/admin/clubs/new', 
      action: () => navigate('/admin/clubs/new'),
      icon: <AddIcon />
    },
    { 
      label: 'Manage Members', 
      path: '/admin/memberships', 
      action: () => navigate('/admin/memberships'),
      icon: <ManageMembersIcon />
    },
    { 
      label: 'Add Member', 
      path: '/admin/add-member', 
      action: () => navigate('/admin/add-member'),
      icon: <AddMemberIcon />
    },
    { 
      label: 'Manage Events', 
      path: '/admin/events', 
      action: () => navigate('/admin/events'),
      icon: <EventsIcon />
    },
    { 
      label: 'Lost and Found', 
      path: '/lostfound', 
      action: () => navigate('/lostfound'),
      icon: <LostFoundIcon />
    },
    { 
      label: 'Skills', 
      path: '/skills', 
      action: () => navigate('/skills'),
      icon: <SkillsIcon />
    },
    { 
      label: 'Doubts', 
      path: '/yourdoubts', 
      action: () => navigate('/yourdoubts'),
      icon: <DoubtsIcon />
    },
    { 
      label: 'Settings', 
      path: '/settings', 
      action: () => navigate('/settings'),
      icon: <SettingsIcon />
    },
  ];

  // Fetch club members from API
  useEffect(() => {
    const fetchClubMembers = async () => {
      try {
        setLoadingMembers(true);
        const response = await api.get('users/club-members/');
        const payload = response?.data ?? response;
        const rawList = Array.isArray(payload)
          ? payload
          : (payload?.results?.data ?? payload?.results ?? payload?.data ?? []);
        const list = Array.isArray(rawList) ? rawList : [];

        // Transform the API response to match the expected format
        const transformedMembers = list.map((member) => {
          const nameRaw = member?.username ?? member?.name ?? '';
          const emailRaw = member?.email ?? '';
          const clubLabel = stripClubSuffix(extractClubLabel(member?.club));

          // Backend returns a stable unique id for membership rows.
          // Use it for UI row identity (keys/selection) to avoid duplicates/null ids.
          const rowId = (member?.row_id ?? '').toString().trim();
          const userIdRaw = member?.user_id ?? member?.id;
          const userId = typeof userIdRaw === 'number' ? userIdRaw : null;
          const clubId = rowId && rowId.includes(':') ? Number(rowId.split(':')[0]) : null;

          return {
          id: rowId || `row:${clubLabel || 'club'}:${emailRaw || nameRaw || Math.random().toString(16).slice(2)}`,
          userId,
          clubId: Number.isFinite(Number(member?.club_id)) ? Number(member?.club_id) : (Number.isFinite(clubId) ? clubId : null),
          name: nameRaw && nameRaw.toString().trim() ? nameRaw : '—',
          email: emailRaw && emailRaw.toString().trim() ? emailRaw : '—',
          club: clubLabel || 'No Club Assigned',
          uniqueCode: member?.membership_member_id ?? member?.club_member_id ?? member?.uniqueCode ?? '—',
          status: normalizeStatusKey(member?.status),
          student_id: member?.student_id,
          college: member?.college,
          year: member?.year,
          department: member?.department,
          image: member?.image,
          role: member?.role,
          };
        });

        setMembers(transformedMembers);
      } catch (error) {
        // console.log(error);
        setMembers([]);
      } finally {
        setLoadingMembers(false);
      }
    };
    fetchClubMembers();
  }, []);

  useEffect(() => {
    setFiltersOpen(!isMobile);
  }, [isMobile]);

  const buildFilterSearch = (next) => {
    const params = new URLSearchParams();
    const setIf = (key, value) => {
      const v = (value ?? '').toString().trim();
      if (v) params.set(key, v);
    };

    if (navFrom === 'clubs') params.set('from', 'clubs');

    setIf('name', next.name);
    setIf('email', next.email);
    setIf('club', next.club);
    setIf('status', next.status);

    const qs = params.toString();
    return qs ? `?${qs}` : '';
  };

  // Hydrate filters from URL ONCE on initial mount.
  // This ensures returning from Edit preserves all filters, and prevents the URL-sync
  // effect from replacing the query string before we read it.
  useEffect(() => {
    if (hydratedFromUrl) return;
    try {
      const params = new URLSearchParams(location.search);
      const fromParam = (params.get('from') || '').toString().trim().toLowerCase();
      const nameParam = params.get('name') || '';
      const emailParam = params.get('email') || '';
      const statusParam = params.get('status') || '';
      const clubParam = params.get('club') || '';

      if (fromParam === 'clubs') setNavFrom('clubs');

      if (nameParam) setNameFilter(nameParam);
      if (emailParam) setEmailFilter(emailParam);
      if (statusParam) setStatusFilter(statusParam);

      if (clubParam) {
        const decoded = stripClubSuffix(decodeURIComponent(clubParam));
        setClubFilter(decoded);
        // Only lock the club filter if it was already present in the URL on initial load.
        // (When the user manually selects a club filter, it gets written to the URL later
        // and must remain clearable.)
        setLockedClubFilter(decoded);
      }
    } catch (_err) {
      // ignore
    } finally {
      setHydratedFromUrl(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydratedFromUrl]);

  // Keep URL in sync with current filters using react-router (so returnTo always includes them).
  useEffect(() => {
    if (!hydratedFromUrl) return;
    const desiredSearch = buildFilterSearch({
      name: nameFilter,
      email: emailFilter,
      club: clubFilter,
      status: statusFilter,
    });
    if (location.search === desiredSearch) return;
    navigate({ pathname: location.pathname, search: desiredSearch }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameFilter, emailFilter, clubFilter, statusFilter, navFrom, location.pathname]);

  const buildReturnTo = () => {
    const search = buildFilterSearch({
      name: nameFilter,
      email: emailFilter,
      club: clubFilter,
      status: statusFilter,
    });
    return `${location.pathname}${search}`;
  };

  const openMemberDetails = (rowId) => {
    const row = members.find((m) => m.id === rowId);
    if (row?.userId != null) {
      const returnTo = buildReturnTo();
      const encoded = encodeURIComponent(returnTo);
      navigate(`/admin/memberships/${row.userId}?returnTo=${encoded}`, { state: { returnTo } });
      return;
    }
    setSnackbar({
      open: true,
      severity: 'error',
      message: 'This membership row is not linked to a user.',
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const clearSelection = () => setSelectedIds([]);

  const openConfirmFor = (action, ids) => {
    const normalizedIds = Array.isArray(ids) ? ids.filter((x) => x != null) : [];
    if (!normalizedIds.length) return;
    setSelectedIds(normalizedIds);
    setConfirmAction(action);
    setConfirmOpen(true);
  };

  // compute filtered members from filters (Name, Email, Club, Status)
  const clubOptions = useMemo(() => {
    const clubs = new Set();
    for (const m of members) {
      const label = (m?.club ?? '').toString().trim();
      if (label) clubs.add(stripClubSuffix(label));
    }
    return Array.from(clubs).sort((a, b) => a.localeCompare(b));
  }, [members]);

  const filteredMembers = useMemo(() => {
    const nameTerm = nameFilter.trim().toLowerCase();
    const emailTerm = emailFilter.trim().toLowerCase();
    const clubKey = normalizeClubKey(clubFilter);
    const statusKey = normalizeStatusKey(statusFilter);

    return members.filter((m) => {
      const name = (m?.name ?? '').toString().toLowerCase();
      const email = (m?.email ?? '').toString().toLowerCase();
      const club = (m?.club ?? '').toString();
      const status = normalizeStatusKey(m?.status);

      const nameMatch = !nameTerm || name.includes(nameTerm);
      const emailMatch = !emailTerm || email.includes(emailTerm);
      const clubMatch = clubFilter ? normalizeClubKey(club) === clubKey : true;
      const statusMatch = statusFilter ? status === statusKey : true;
      return nameMatch && emailMatch && clubMatch && statusMatch;
    });
  }, [members, nameFilter, emailFilter, clubFilter, statusFilter]);

  const hasActiveFilters =
    nameFilter.trim() !== '' ||
    emailFilter.trim() !== '' ||
    clubFilter !== '' ||
    statusFilter !== '';

  const clearFilters = () => {
    setNameFilter('');
    setEmailFilter('');
    setStatusFilter('');

    // If the club filter was applied via redirect query param, keep it.
    if (!lockedClubFilter) {
      setClubFilter('');
    }
  };

  const handleSelectOpen = (field) => (event) => {
    const width = event.currentTarget?.getBoundingClientRect().width;
    if (width) {
      setMenuWidths((prev) => ({ ...prev, [field]: width }));
    }
    setOpenSelectField(field);
  };

  const handleSelectClose = () => {
    setOpenSelectField(null);
  };

  const getMenuProps = (field) => {
    const width = menuWidths[field];
    return {
      disablePortal: false,
      disableScrollLock: true,
      container: mainContentRef.current,
      sx: {
        zIndex: 1300,
      },
      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
      transformOrigin: { vertical: 'top', horizontal: 'left' },
      marginThreshold: null,
      transitionDuration: 120,
      PaperProps: {
        sx: {
          mt: 0,
          width: width || undefined,
          minWidth: width || undefined,
          maxWidth: width || undefined,
        },
      },
    };
  };

  const exportToPDF = () => {
    const records = filteredMembers;
    const today = new Date();
    const dateStamp = today.toISOString().split('T')[0];
    const timeStamp = today.toLocaleString();

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('Club Members Export', 40, 42);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Generated: ${timeStamp}`, 40, 62);
    doc.text(`Records: ${records.length}`, 40, 78);

    const activeFilterLabels = [];
    if (nameFilter.trim()) activeFilterLabels.push(`Name: ${nameFilter.trim()}`);
    if (emailFilter.trim()) activeFilterLabels.push(`Email: ${emailFilter.trim()}`);
    if (clubFilter) activeFilterLabels.push(`Club: ${clubFilter}`);
    if (statusFilter) activeFilterLabels.push(`Status: ${statusFilter}`);

    if (activeFilterLabels.length > 0) {
      const filtersLine = `Filters: ${activeFilterLabels.join('  •  ')}`;
      const lines = doc.splitTextToSize(filtersLine, 760);
      doc.text(lines, 40, 94);
    }

    const columns = [
      'Name',
      'Email',
      'Club',
      'Unique Code',
      'Status',
      'Student ID',
      'College',
      'Year',
      'Department',
    ];

    const rows = records.map((m) => [
      m?.name ?? '—',
      m?.email ?? '—',
      m?.club ?? '—',
      m?.uniqueCode ?? '—',
      (m?.status ?? 'pending').toString().toUpperCase(),
      m?.student_id ?? '—',
      m?.college ?? '—',
      m?.year ?? '—',
      m?.department ?? '—',
    ]);

    const tableMarginX = 22;
    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: activeFilterLabels.length > 0 ? 120 : 105,
      theme: 'grid',
      margin: { left: tableMarginX, right: tableMarginX },
      tableWidth: 'auto',
      styles: {
        font: 'helvetica',
        fontSize: 7.2,
        cellPadding: 3,
        overflow: 'linebreak',
        valign: 'top',
      },
      headStyles: {
        fillColor: [13, 28, 79],
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 125 },
        2: { cellWidth: 90 },
        3: { cellWidth: 70, fontStyle: 'normal' },
        4: { cellWidth: 55 },
        5: { cellWidth: 70 },
        6: { cellWidth: 85 },
        7: { cellWidth: 45 },
        8: { cellWidth: 150 },
      },
      didDrawPage: () => {
        const pageCount = doc.getNumberOfPages();
        const pageNumber = doc.getCurrentPageInfo().pageNumber;
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(
          `Page ${pageNumber} of ${pageCount}`,
          doc.internal.pageSize.getWidth() - tableMarginX,
          doc.internal.pageSize.getHeight() - 18,
          { align: 'right' }
        );
      },
    });

    doc.save(`club_members_export_${dateStamp}.pdf`);
  };

  // visible rows are the filtered members (this powers select-all behavior)
  const visibleIds = filteredMembers.map((m) => m.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someSelected = selectedIds.some((id) => visibleIds.includes(id)) && !allSelected;

  const handleSelectAllClick = (ev) => {
    // compute current selection state for visible rows (use fresh values)
    const currentlySelectedVisible = visibleIds.filter((id) => selectedIds.includes(id)).length;
    const noneSelectedVisible = currentlySelectedVisible === 0;
    const someButNotAllSelectedVisible = currentlySelectedVisible > 0 && currentlySelectedVisible < visibleIds.length;

    // If some (but not all) visible rows are selected, treat click as "clear visible selection"
    if (someButNotAllSelectedVisible) {
      setSelectedIds((s) => s.filter((id) => !visibleIds.includes(id)));
      return;
    }

    // Otherwise toggle select/deselect visible rows
    if (noneSelectedVisible && ev.target.checked) {
      // select all visible
      setSelectedIds((s) => Array.from(new Set([...s, ...visibleIds])));
    } else {
      // deselect all visible
      setSelectedIds((s) => s.filter((id) => !visibleIds.includes(id)));
    }
  };

  const handleApprove = async () => {
    openConfirmFor('approve', selectedIds);
  };

  const handleSuspend = async () => {
    openConfirmFor('suspend', selectedIds);
  };

  const handleConfirmClose = () => {
    if (actionLoading) return;
    setConfirmOpen(false);
    setConfirmAction(null);
  };

  const handleConfirmOk = async () => {
    if (!selectedIds.length || !confirmAction) return;

    const newStatus = confirmAction === 'approve' ? 'active' : 'inactive';
    const selectedRows = selectedIds
      .map((rowId) => members.find((m) => m.id === rowId))
      .filter(Boolean);

    const selectedUserIds = selectedRows
      .map((m) => m.userId)
      .filter((v) => typeof v === 'number');

    // Update Club.members JSON status for each selected membership row.
    const membershipRequests = selectedRows
      .filter((m) => Number.isFinite(m.clubId) && m.clubId != null)
      .map((m) => {
        const memberId = (m?.student_id ?? '').toString().trim();
        const email = (m?.email ?? '').toString().trim();
        return api.patch(`events/clubmembers/${m.clubId}/`, {
          action: 'set_status',
          member_id: memberId,
          email,
          status: newStatus,
        });
      });

    const count = selectedRows.length;

    setActionLoading(true);
    try {
      // 1) Update user accounts when we can resolve them.
      if (selectedUserIds.length > 0) {
        await api.put('users/update-users/', {
          user_ids: selectedUserIds,
          status: newStatus,
        });
      }

      // 2) Update membership status in Club table.
      if (membershipRequests.length > 0) {
        await Promise.allSettled(membershipRequests);
      }

      setMembers((prev) =>
        prev.map((m) => (selectedIds.includes(m.id) ? { ...m, status: newStatus } : m))
      );
      setSelectedIds([]);

      setSnackbar({
        open: true,
        severity: 'success',
        message:
          confirmAction === 'approve'
            ? `Approved ${count} club member${count > 1 ? 's' : ''}.`
            : `Suspended ${count} club member${count > 1 ? 's' : ''}.`,
      });

      setConfirmOpen(false);
      setConfirmAction(null);
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Action failed. Please try again.',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleBackNavigation = () => {
    if (navFrom === 'clubs') {
      navigate('/admin/clubs');
      return;
    }
    navigate('/admin/dashboard');
  };

  const statusChipProps = (statusRaw) => {
    const status = normalizeStatusKey(statusRaw);
    if (status === 'active') return { label: 'Active', color: 'success' };
    if (status === 'pending') return { label: 'Pending', color: 'warning' };
    if (status === 'inactive' || status === 'suspended') return { label: 'Inactive', color: 'error' };
    return { label: statusRaw || 'Unknown', color: 'default' };
  };

  const renderDesktopTable = () => (
    <TableContainer className="mcm-table-container" component={Box}>
      <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleSelectAllClick}
                  inputProps={{ 'aria-label': 'select all displayed members' }}
                />
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Club Name</TableCell>
              <TableCell>Unique Code</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingMembers ? (
              Array.from({ length: 8 }).map((_, idx) => (
                <TableRow key={idx}>
                  <TableCell padding="checkbox"><Skeleton variant="circular" width={22} height={22} /></TableCell>
                  <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="55%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="45%" /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={90} height={24} /></TableCell>
                  <TableCell align="right"><Skeleton variant="rounded" width={42} height={34} /></TableCell>
                </TableRow>
              ))
            ) : filteredMembers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Box className="mcm-empty">No members matched the current filters.</Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredMembers.map((m) => {
                const chip = statusChipProps(m.status);
                return (
                  <TableRow key={m.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedIds.includes(m.id)} onChange={() => toggleSelect(m.id)} />
                    </TableCell>
                    <TableCell>{m.name}</TableCell>
                    <TableCell>{m.email}</TableCell>
                    <TableCell>{m.club}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{m.uniqueCode}</TableCell>
                    <TableCell>
                      <Chip label={chip.label} size="small" color={chip.color} sx={{ fontWeight: 900 }} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        className="mcm-actions-btn"
                        aria-label="member details"
                        onClick={() => openMemberDetails(m.id)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
      </Table>
    </TableContainer>
  );

  const renderMobileCards = () => (
    <Box className="mcm-card-list">
      {loadingMembers ? (
        Array.from({ length: 6 }).map((_, idx) => (
          <Paper key={idx} className="mcm-member-card" elevation={0}>
            <Skeleton variant="text" width="72%" height={28} />
            <Skeleton variant="text" width="88%" height={22} />
            <Skeleton variant="rounded" height={110} sx={{ mt: 1 }} />
          </Paper>
        ))
      ) : filteredMembers.length === 0 ? (
        <Box className="mcm-empty">No members matched the current filters.</Box>
      ) : (
        filteredMembers.map((m) => {
          const chip = statusChipProps(m.status);
          return (
            <Paper key={m.id} className="mcm-member-card" elevation={0}>
              <Box className="mcm-member-card-header">
                <Box className="mcm-member-card-primary">
                  <Checkbox
                    checked={selectedIds.includes(m.id)}
                    onChange={() => toggleSelect(m.id)}
                    inputProps={{ 'aria-label': `select ${m.name}` }}
                  />
                  <Box>
                    <Typography variant="subtitle1" className="mcm-member-name">{m.name}</Typography>
                    <Typography variant="body2" className="mcm-member-email">{m.email}</Typography>
                  </Box>
                </Box>
                <Chip label={chip.label} size="small" color={chip.color} sx={{ fontWeight: 900 }} />
              </Box>

              <Box className="mcm-member-meta">
                <Box className="mcm-field">
                  <Typography variant="caption" className="mcm-label">Club</Typography>
                  <Typography variant="body2" className="mcm-value">{m.club}</Typography>
                </Box>
                <Box className="mcm-field">
                  <Typography variant="caption" className="mcm-label">Unique Code</Typography>
                  <Typography variant="body2" className="mcm-value">{m.uniqueCode}</Typography>
                </Box>
                <Box className="mcm-field">
                  <Typography variant="caption" className="mcm-label">Student ID</Typography>
                  <Typography variant="body2" className="mcm-value">{m.student_id || '—'}</Typography>
                </Box>
                <Box className="mcm-field">
                  <Typography variant="caption" className="mcm-label">Year</Typography>
                  <Typography variant="body2" className="mcm-value">{m.year || '—'}</Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 1.25, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="outlined"
                  className="mcm-filters-toggle"
                  onClick={() => openMemberDetails(m.id)}
                >
                  Details
                </Button>
              </Box>
            </Paper>
          );
        })
      )}
    </Box>
  );

  return (
    <div className="admin-dashboard manage-club-members">
      {/* Pass sidebarItems to Navbar for mobile */}
      <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} />
      <div className="dashboard-layout">
        {/* Only show sidebar on desktop */}
        {!isMobile && <GlobalSidebar />}
        <main className="dashboard-main" style={{ 
          marginLeft: isMobile ? 0 : undefined,
          width: isMobile ? '100%' : undefined 
        }}>
          <Container ref={mainContentRef} maxWidth="xl" className="dashboard-container">
            <header className="mcm-page-header">
              <Box className="mcm-header-left">
                <Box className="mcm-breadcrumb-row">
                  <Tooltip title="Back">
                    <IconButton onClick={handleBackNavigation} className="mcm-back-icon" size="medium" aria-label="Back">
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Breadcrumbs
                    className="mcm-breadcrumbs"
                    separator={<NavigateNextIcon fontSize="small" />}
                    aria-label="breadcrumb"
                  >
                    <Link
                      underline="hover"
                      color="inherit"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/admin/dashboard');
                      }}
                      href="/admin/dashboard"
                      className="mcm-breadcrumb-link"
                    >
                      Dashboard
                    </Link>
                    {navFrom === 'clubs' && (
                      <Link
                        underline="hover"
                        color="inherit"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/admin/clubs');
                        }}
                        href="/admin/clubs"
                        className="mcm-breadcrumb-link"
                      >
                        Clubs
                      </Link>
                    )}
                    <Typography color="text.primary" className="mcm-breadcrumb-current">
                      Club Members
                    </Typography>
                  </Breadcrumbs>
                </Box>

                <Box className="mcm-title-wrap">
                  <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" className="mcm-page-title">
                    Manage Club Members
                  </Typography>
                  <Typography variant="body2" className="mcm-page-subtitle" color="text.secondary">
                    View member details and manage club membership.
                  </Typography>
                </Box>
              </Box>

              <Box className="mcm-header-actions">
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={exportToPDF}
                  className="mcm-export-btn"
                  disabled={loadingMembers || members.length === 0}
                >
                  Export PDF ({filteredMembers.length})
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/admin/memberships/new')}
                  className="mcm-btn-primary"
                >
                  Add New Member
                </Button>

                {selectedIds.length > 0 && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<CheckCircleIcon />}
                      onClick={handleApprove}
                      disabled={actionLoading}
                      className="mcm-btn-approve"
                    >
                      Approve Selected
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleSuspend}
                      disabled={actionLoading}
                      className="mcm-btn-suspend"
                    >
                      Suspend Selected
                    </Button>
                  </>
                )}
              </Box>
            </header>

            <Paper className="mcm-card" elevation={0}>
              <Box className="mcm-card-header">
                <Box>
                  <Typography variant="h6" className="mcm-card-title">
                    All Members
                  </Typography>
                  <Typography className="mcm-card-subtitle">
                    {loadingMembers
                      ? 'Loading…'
                      : (filteredMembers.length === members.length
                        ? `${members.length} member${members.length === 1 ? '' : 's'}`
                        : `Showing ${filteredMembers.length} of ${members.length} members`)}
                  </Typography>
                </Box>

                <Box className="mcm-card-header-actions">
                  {selectedIds.length > 0 && (
                    <>
                      <Chip
                        className="mcm-selected-pill"
                        label={`${selectedIds.length} selected`}
                        size="small"
                      />
                      <Button
                        variant="text"
                        size="small"
                        className="mcm-clear-selection"
                        onClick={clearSelection}
                        disabled={actionLoading}
                      >
                        Clear selection
                      </Button>
                    </>
                  )}

                  {isMobile && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setFiltersOpen((v) => !v)}
                      className="mcm-filters-toggle"
                    >
                      {filtersOpen ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                  )}
                </Box>
              </Box>

              <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
                <Box className="mcm-filters-bar">
                  <Box className="mcm-filters-row">
                    <TextField
                      label="Name"
                      value={nameFilter}
                      onChange={(e) => setNameFilter(e.target.value)}
                      size="small"
                      placeholder="Search by name…"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      label="Email"
                      value={emailFilter}
                      onChange={(e) => setEmailFilter(e.target.value)}
                      size="small"
                      placeholder="Search by email…"
                    />

                    <TextField
                      select
                      label="Club"
                      value={clubFilter}
                      onChange={(e) => setClubFilter(e.target.value)}
                      size="small"
                      sx={{ minWidth: 160 }}
                      SelectProps={{
                        MenuProps: getMenuProps('club'),
                        onOpen: handleSelectOpen('club'),
                        onClose: handleSelectClose,
                        open: openSelectField === 'club',
                      }}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {clubOptions.map((c) => (
                        <MenuItem key={c} value={c}>{c}</MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      select
                      label="Status"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      size="small"
                      sx={{ minWidth: 160 }}
                      SelectProps={{
                        MenuProps: getMenuProps('status'),
                        onOpen: handleSelectOpen('status'),
                        onClose: handleSelectClose,
                        open: openSelectField === 'status',
                      }}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </TextField>

                    <Button
                      variant="text"
                      size="small"
                      onClick={clearFilters}
                      className="mcm-clear-btn"
                      disabled={!hasActiveFilters}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
                <Divider />
              </Collapse>

              <Box sx={{ p: isMobile ? 0 : 0 }}>
                {!isMobile ? renderDesktopTable() : renderMobileCards()}
              </Box>
            </Paper>
          </Container>
        </main>
      </div>
      <PostLoginFooter />

      <Dialog
        open={confirmOpen}
        onClose={handleConfirmClose}
        aria-labelledby="confirm-action-title"
        aria-describedby="confirm-action-description"
      >
        <DialogTitle id="confirm-action-title">
          {confirmAction === 'approve' ? 'Approve registrations?' : 'Suspend registrations?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-action-description">
            {confirmAction === 'approve'
              ? `Are you sure you want to approve ${selectedIds.length} club member${selectedIds.length === 1 ? '' : 's'}?`
              : `Are you sure you want to suspend ${selectedIds.length} club member${selectedIds.length === 1 ? '' : 's'}?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose} disabled={actionLoading} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmOk}
            variant="contained"
            disabled={actionLoading || selectedIds.length === 0}
            sx={{ textTransform: 'none' }}
          >
            {actionLoading ? 'Please wait…' : 'OK'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ fontWeight: 700 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}